<template>
  <div class="table-scroll-wrapper">
    <table class="table table-stack">
      <thead>
        <tr>
          <th class="col-1 text-center subject">Sort</th>
          <th class="col-8 subject">Name</th>
          <th class="col-1 text-center subject">Unit</th>
          <th class="col-1 text-center subject">Status</th>
          <th class="col-1 subject"></th>
        </tr>
      </thead>
      <draggable
        v-model="localStack"
        tag="tbody"
        handle=".drag-handle"
        :animation="200"
        @start="dragging = true"
        @end="handleDragEnd"
      >
        <tr v-for="item in localStack" :key="item.id" :class="{ 'is-dragging': dragging }">
          <td class="col-1 text-center">
            <font-awesome-icon
              v-if="item.status.toUpperCase() === 'PUBLISHED'"
              :icon="faBars"
              class="drag-handle"
              :class="{ dragging }"
            />
          </td>
          <td class="col-8">
            <div class="text__stack-title">{{ item.title }}</div>
            <div
              v-if="
                item.status.toUpperCase() === 'PUBLISHED' && item.published_learning_unit_count < 3
              "
              class="warning-text"
            >
              Explore page shows under 3 micro-credentials due to status changes.
            </div>
          </td>
          <td class="col-1 text-center">{{ item.learning_unit_count }}</td>
          <td class="col-1 text-center text-uppercase">
            <span class="text__stack-status" :class="`text-status-${item.status.toLowerCase()}`">
              {{ item.status }}
            </span>
          </td>
          <td class="col-1">
            <b-button variant="outline-primary" @click="editStcakItem(item)">Edit</b-button>
          </td>
        </tr>
      </draggable>
    </table>
  </div>
</template>

<script>
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import draggable from 'vuedraggable';
import Stack from '@/models/Stack';

export default {
  name: 'DraggableTable',
  components: {
    FontAwesomeIcon,
    draggable
  },
  props: {
    stack: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      faBars,
      dragging: false,
      localStack: [],
      stackStatusError: []
    };
  },
  watch: {
    stack: {
      immediate: true,
      handler(newValue) {
        this.localStack = [...newValue];
      }
    }
  },
  mounted() {
    this.checkStackUnitStatus();
  },
  methods: {
    editStcakItem(item) {
      this.$emit('edit', { ...item });
    },
    async handleDragEnd() {
      this.dragging = false;

      try {
        // Sort the stacks to ensure drafts are at the bottom
        this.localStack = [
          ...this.localStack.filter((stack) => stack.status.toUpperCase() === 'PUBLISHED'),
          ...this.localStack.filter((stack) => stack.status.toUpperCase() !== 'PUBLISHED')
        ];

        // Calculate base position from current page
        const basePosition = (this.$parent.currentPage - 1) * this.$parent.perPage;

        // Reorder the stacks with paginated position
        const orderedStacks = this.localStack.map((stack, index) => ({
          id: stack.id,
          position: basePosition + index
        }));

        // Update the order of the stacks
        await Stack.api().reorder({ orderedStacks });

        // Emit the reordered stacks to the parent component
        this.$emit('reorder', this.localStack);

        // Show a success toast
        this.$toast.open({
          message: 'Stack order updated successfully',
          type: 'success',
          pauseOnHover: true,
          duration: 5000
        });
      } catch (error) {
        console.error('Error reordering stacks:', error);
        // Revert to original order if API call fails
        this.localStack = [...this.stack];

        // Show an error toast
        this.$toast.open({
          message: 'Failed to update stack order',
          type: 'error',
          pauseOnHover: true,
          duration: 5000
        });
      }
    },
    checkStackUnitStatus() {
      if (!this.localStack?.length) return;

      this.stackStatusError = this.localStack.filter(
        (item) => item.status.toLowerCase() === 'published' && item.learning_unit_count < 3
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.table-scroll-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

.table-stack {
  min-width: 544px;
  margin-bottom: 0;
  td {
    vertical-align: middle;
  }
}

.drag-handle {
  cursor: move;
  color: #6c757d;
  transition: all 0.2s ease;
  display: inline-block;
  padding: 8px;
  font-size: 16px;

  &.dragging {
    transform: scale(1.1);
    color: #007bff;
  }

  &:hover {
    color: #007bff;
  }
}

tr.dragging {
  opacity: 0.6;
  background: #f8f9fa;
}

th.subject {
  text-transform: capitalize;
  background: none;
}

.text__stack-status {
  font-size: 12px;
  font-weight: 700;
  padding: 0 8px;
  border-radius: 10rem;
}

.text-status-draft {
  background: #7a7b971a;
  color: #7a7b97;
}

.text-status-published {
  background: #e5f9ed;
  color: #1bc943;
}

.text-feedback {
  color: red;
  font-size: 14px;
  margin: 0;
}

.warning-text {
  color: #f83245;
  font-size: 12px;
  margin-top: 4px;
}

.text__stack-title {
  display: -webkit-box;
  -webkit-line-clamp: 2; // Limit to 2 lines
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5em;
  max-height: 3em;
}
</style>
